/* eslint-disable */
import { ORG, loadLogo } from "./brand";

// jsPDF is ~150 kB gzipped and only needed the moment someone clicks
// "Download Statement", so it is pulled in as its own chunk rather than
// riding along in the main bundle.
const loadPdfLibs = async () => {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  return { jsPDF, autoTable: autoTableModule.default ?? autoTableModule.autoTable };
};

const PAGE_W = 210; // A4 portrait, mm
const PAGE_H = 297;
const MARGIN_X = 16;
const LETTERHEAD_H = 50; // space the letterhead occupies at the top of every page
const CONTENT_W = PAGE_W - MARGIN_X * 2; // full printable width the table fills

const pad = (n) => String(n).padStart(2, "0");

// 27/04/25 — matches the printed statement.
export const shortDate = (value) => {
  const d = new Date(value);
  if (!value || isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
};

// 2025/05/28 — the "Statement as at" format.
export const isoDate = (value) => {
  const d = value ? new Date(value) : new Date();
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
};

const money = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// The upload files label fund contributions in a few ways ("FUND DED",
// "FUND DEDUCTION", …). Anything that reads as a fund deduction is a
// "Contribution" on the statement; anything else keeps its own label.
export const describeTransaction = (transaction_type) =>
  !transaction_type || /fund\s*ded/i.test(transaction_type)
    ? "Contribution"
    : transaction_type;

/** Newest first, by the row's actual date (period strings don't sort). */
export const sortRowsNewestFirst = (rows = []) =>
  [...rows].sort((a, b) => new Date(b.date) - new Date(a.date));

const LOGO_H = 26; // height the mark is drawn at in the letterhead
const aspect = (logo) => (logo?.height ? logo.width / logo.height : 1);

const GREEN = [27, 94, 58];

/**
 * Reproduces the official UPNMG-FUND letterhead: green spine down the left
 * edge, mark and stacked wordmark top-left, italic contact block on the
 * right, italic ref line, and a heavy green rule closing the block.
 */
function drawLetterhead(doc, logo) {
  // Green spine down the left edge.
  doc.setFillColor(...GREEN);
  doc.rect(8, 8, 2.2, PAGE_H - 16, "F");

  // Mark, then the wordmark typeset beside it (the asset is the mark alone).
  let textX = MARGIN_X;
  if (logo) {
    const width = LOGO_H * aspect(logo);
    doc.addImage(logo.dataUrl, "PNG", MARGIN_X, 8, width, LOGO_H);
    textX = MARGIN_X + width + 3;
  }

  doc.setTextColor(...GREEN);
  doc.setFont("times", "bold");
  doc.setFontSize(24);
  doc.text(ORG.name[0], textX, 18);
  doc.text(ORG.name[1], textX, 27.5);
  doc.setFont("times", "bolditalic");
  doc.setFontSize(8.5);
  doc.text(ORG.tagline, textX + 0.5, 32.5);

  // Contact block (right) — italic throughout, green labels, as on the
  // letterhead.
  const rightX = 116;
  const contact = [
    ["Postal Address: ", ORG.postalAddress, 13],
    ["Tel", `: ${ORG.tel}`, 19],
    ["E-mail: ", ORG.email, 25],
  ];
  doc.setFontSize(8);
  contact.forEach(([label, value, y]) => {
    doc.setFont("times", "bolditalic");
    doc.setTextColor(...GREEN);
    doc.text(label, rightX, y);
    const labelWidth = doc.getTextWidth(label); // measure in the label's own face
    doc.setFont("times", "italic");
    doc.setTextColor(20, 20, 20);
    doc.text(value, rightX + labelWidth, y);
  });

  // Ref line — one row, italic, as on the letterhead.
  doc.setTextColor(0, 0, 0);
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.text(`Our Ref: ${ORG.ourRef}`, MARGIN_X, 39);
  doc.text("Your Ref:………………….", 88, 39);
  doc.text(`Date: ${todayLongDate()}`, 138, 39);

  doc.setDrawColor(...GREEN);
  doc.setLineWidth(1.2);
  doc.line(MARGIN_X, 42.5, PAGE_W - MARGIN_X, 42.5);
}

function todayLongDate() {
  const d = new Date();
  const months = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
  ];
  return `${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}`;
}

/** Faded mark across the middle of the page. */
function drawWatermark(doc, logo) {
  if (!logo) return;
  const height = 120;
  const width = height * aspect(logo);
  const x = (PAGE_W - width) / 2;
  const y = (PAGE_H - height) / 2;
  const gs = doc.GState ? doc.GState({ opacity: 0.08 }) : null;
  if (gs) doc.setGState(gs);
  doc.addImage(logo.dataUrl, "PNG", x, y, width, height);
  if (gs) doc.setGState(doc.GState({ opacity: 1 }));
}

const BODY_FONT = 10.5;
const CELL_PAD_X = 2; // per side, matching styles.cellPadding below
const DATE_W = 30;
const DEDUCTION_W = 40;
const DESCRIPTION_MIN_W = 32; // "Contribution" needs ~19 mm, so this is roomy

/**
 * Widens the REFERENCE column to whatever its longest value actually needs, so
 * a reference always prints on one line. References come straight off the
 * upload file and can be anything from "MAY25" to a full bank narration, so a
 * fixed width either wraps the long ones or strands the short ones.
 *
 * Takes the space from DESCRIPTION (down to a floor); if even that isn't
 * enough, hands back a reduced font size for the column rather than letting
 * autoTable break the text.
 */
function measureReferenceColumn(doc, references) {
  doc.setFontSize(BODY_FONT);
  doc.setFont("times", "bold"); // the heading is bold, so it measures wider
  let widest = doc.getTextWidth("REFERENCE");
  doc.setFont("times", "normal");
  widest = references.reduce(
    (max, text) => Math.max(max, doc.getTextWidth(String(text))),
    widest
  );

  const wanted = widest + CELL_PAD_X * 2 + 1; // +1 so the glyphs never touch the edge
  const available = CONTENT_W - DATE_W - DEDUCTION_W - DESCRIPTION_MIN_W;
  const width = Math.min(Math.max(wanted, 28), available);

  return {
    width,
    // Only ever shrinks — 1 when the text already fits.
    fontSize: BODY_FONT * Math.min(1, (width - CELL_PAD_X * 2 - 1) / (widest || 1)),
  };
}

/**
 * Builds the member fund statement and hands back the jsPDF document.
 *
 * member - { staff_id, name, facility, department, district, region }
 * rows   - fund rows as returned by GET /funds (date, period, deduction, …)
 */
export async function buildFundStatement({ member, rows }) {
  const [logo, { jsPDF, autoTable }] = await Promise.all([loadLogo(), loadPdfLibs()]);
  // compress: the logo embeds as a raw RGB stream plus an alpha SMask, which
  // is ~800 kB uncompressed for a statement that is otherwise a few kB.
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const ordered = sortRowsNewestFirst(rows);
  const total = ordered.reduce((sum, r) => sum + Number(r.deduction || 0), 0);
  const references = ordered.map((r) => r.reference || r.period || "");

  // Page one is drawn by hand (watermark → letterhead → member block) so the
  // wash sits behind the text. autoTable's willDrawPage covers pages 2+.
  drawWatermark(doc, logo);
  drawLetterhead(doc, logo);

  // Member block — page one only, like the printed statement.
  let y = 55;
  const label = (text, value) => {
    doc.setFont("times", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(0, 0, 0);
    doc.text(text, MARGIN_X, y);
    doc.text(String(value ?? "—"), MARGIN_X + 40, y);
    y += 6;
  };

  const facility = [member?.facility, member?.department].filter(Boolean).join(" Dept of ");
  label("Staff ID", member?.staff_id);
  label("Name", member?.name);
  label("Facility", facility || member?.facility);
  label("District", member?.district);
  label("Region", member?.region);
  label("Statement as at", isoDate());

  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.8);
  doc.line(MARGIN_X, y + 2, PAGE_W - MARGIN_X, y + 2);

  const reference = measureReferenceColumn(doc, references);

  autoTable(doc, {
    startY: y + 10,
    margin: { top: LETTERHEAD_H, left: MARGIN_X, right: MARGIN_X },
    theme: "plain",
    // Sum of the fixed column widths below — the table spans the full printable
    // width so the four columns sit evenly across the page instead of huddling
    // against the left margin.
    tableWidth: CONTENT_W,
    styles: {
      font: "times",
      fontSize: BODY_FONT,
      cellPadding: { top: 1, bottom: 1, left: CELL_PAD_X, right: CELL_PAD_X },
      textColor: [0, 0, 0],
      overflow: "linebreak",
    },
    // Header cells sit over their own column's alignment, so DEDUCTION heads
    // its right-aligned figures instead of drifting left of them.
    headStyles: { fontStyle: "bold" },
    // The four widths always sum to tableWidth (178 = 210 − 2 × 16), and each
    // clears what its own heading measures, so no heading ever wraps onto a
    // second line ("REFERENC" / "E"). Minimums at these fonts, in mm:
    //   DATE 17.2 · DESCRIPTION 29.6 · REFERENCE 26.7 · DEDUCTION (¢) 31.8
    // REFERENCE is sized to its longest value (see measureReferenceColumn) so
    // it stays on one line; DESCRIPTION absorbs the remainder. DATE and
    // REFERENCE are centred — left-aligning them would strand short values at
    // the edge of their cells, away from the heading they belong under.
    columnStyles: {
      0: { cellWidth: DATE_W, halign: "center" },
      1: { cellWidth: CONTENT_W - DATE_W - DEDUCTION_W - reference.width },
      2: {
        cellWidth: reference.width,
        halign: "center",
        fontSize: reference.fontSize,
        overflow: "visible",
      },
      3: { cellWidth: DEDUCTION_W, halign: "right" },
    },
    // One header row: the cedi sign rides with DEDUCTION rather than sitting
    // on a second row, which used to open a blank gap under REFERENCE before
    // its values began.
    head: [["DATE", "DESCRIPTION", "REFERENCE", "DEDUCTION (¢)"]],
    body: ordered.map((r, i) => [
      shortDate(r.date),
      describeTransaction(r.transaction_type),
      references[i], // same strings the column was measured against
      money(r.deduction),
    ]),
    foot: [["TOTAL", "", "", money(total)]],
    showFoot: "lastPage", // otherwise the TOTAL row repeats at the foot of every page
    footStyles: { fontStyle: "bold", fontSize: 11, halign: "left", lineWidth: { top: 0.6 }, lineColor: GREEN },
    // columnStyles outrank footStyles, so the foot's alignment is set here:
    // TOTAL stays hard left under DATE, its figure right under DEDUCTION.
    didParseCell: (data) => {
      if (data.section !== "foot") return;
      if (data.column.index === 0) data.cell.styles.halign = "left";
      if (data.column.index === 3) data.cell.styles.halign = "right";
    },
    willDrawPage: (data) => {
      if (data.pageNumber === 1) return; // already drawn above
      drawWatermark(doc, logo);
      drawLetterhead(doc, logo);
    },
  });

  return doc;
}

export async function downloadFundStatement({ member, rows }) {
  const doc = await buildFundStatement({ member, rows });
  const id = member?.staff_id || "member";
  doc.save(`UPNMG-FUND-Statement-${id}-${isoDate().replace(/\//g, "")}.pdf`);
}
